import React from 'react';
import { motion } from 'framer-motion';
import { HiXMark, HiCalendar, HiUser, HiShieldCheck } from 'react-icons/hi2';
import { calculateRiskLevel } from '../../services/productAuditService';

const AuditDetailsModal = ({ isOpen, onClose, audit }) => {
  if (!isOpen || !audit) return null;

  const formatDate = date => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const riskLevel = calculateRiskLevel(audit);
  const riskColors = {
    Critical: 'bg-red-100 text-red-800',
    High: 'bg-orange-100 text-orange-800',
    Medium: 'bg-yellow-100 text-yellow-800',
    Low: 'bg-blue-100 text-blue-800',
    None: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-black">
              {audit.submissionTitle || audit.productName || 'Untitled Submission'}
            </h2>
            <p className="text-sm text-gray-600">
              {audit.organization || 'N/A'} • {audit.projectStage}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <HiCalendar className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Audit Date</p>
                <p className="text-sm font-medium text-gray-900">{formatDate(audit.auditDate)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <HiUser className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Organization</p>
                <p className="text-sm font-medium text-gray-900">{audit.organization || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <HiShieldCheck className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Risk Level</p>
                <p
                  className={`inline-block rounded-full px-2 py-1 text-sm font-medium ${riskColors[riskLevel] || riskColors.None}`}
                >
                  {riskLevel}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {audit.findings && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">Findings</h3>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <p className="whitespace-pre-wrap text-sm text-gray-700">{audit.findings}</p>
                </div>
              </div>
            )}

            {audit.gaps && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">Gaps</h3>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <p className="whitespace-pre-wrap text-sm text-gray-700">{audit.gaps}</p>
                </div>
              </div>
            )}

            {audit.risks && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">Risks</h3>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <p className="whitespace-pre-wrap text-sm text-gray-700">{audit.risks}</p>
                </div>
              </div>
            )}

            {audit.improvementAreas && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">Improvement Areas</h3>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <p className="whitespace-pre-wrap text-sm text-gray-700">
                    {audit.improvementAreas}
                  </p>
                </div>
              </div>
            )}

            {audit.recommendations && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">Recommendations</h3>
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <p className="whitespace-pre-wrap text-sm text-gray-700">
                    {audit.recommendations}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuditDetailsModal;
